import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TYPE_COLORS = {
  GROUP_OBLIGOR: "bg-zinc-700 text-white",
  SHAREHOLDER: "bg-[#0055FF] text-white",
  GUARANTOR: "bg-[#F5A623] text-black",
  COLLATERAL: "bg-emerald-700 text-white",
  ADDRESS: "bg-zinc-500 text-white",
  DIRECTOR: "bg-purple-700 text-white",
};

export default function ConnectedObligors() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/connected-obligors").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const filtered = useMemo(() => filter === "ALL" ? data : data.filter((d) => d.connection_type === filter), [data, filter]);

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="connected-obligors-page">
      <PageHeader
        title="Connected Obligor"
        subtitle="Deteksi borrower yang terhubung melalui group, shareholder, guarantor, collateral, alamat, atau direktur"
      />

      <div className="mb-4 flex items-center gap-3">
        <span className="text-xs text-muted-foreground uppercase tracking-wider">Filter:</span>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="connection-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua tipe</SelectItem>
            <SelectItem value="GROUP_OBLIGOR">Group Obligor</SelectItem>
            <SelectItem value="SHAREHOLDER">Shareholder</SelectItem>
            <SelectItem value="GUARANTOR">Guarantor</SelectItem>
            <SelectItem value="COLLATERAL">Collateral</SelectItem>
            <SelectItem value="ADDRESS">Alamat</SelectItem>
            <SelectItem value="DIRECTOR">Direktur</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="mono">{filtered.length} koneksi</Badge>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : (
        <DataTable
          testId="connected-table"
          searchKeys={["borrower_a_name", "borrower_b_name", "via"]}
          columns={[
            { header: "Borrower A", cell: (r) => (
              <button className="text-left hover:underline" onClick={() => navigate(`/borrowers/${r.borrower_a_id}`)}>
                <div className="font-medium text-xs">{r.borrower_a_name}</div>
                <div className="mono text-[10px] text-muted-foreground">{r.borrower_a_id}</div>
              </button>
            ) },
            { header: "Borrower B", cell: (r) => (
              <button className="text-left hover:underline" onClick={() => navigate(`/borrowers/${r.borrower_b_id}`)}>
                <div className="font-medium text-xs">{r.borrower_b_name}</div>
                <div className="mono text-[10px] text-muted-foreground">{r.borrower_b_id}</div>
              </button>
            ) },
            { header: "Tipe Koneksi", cell: (r) => (
              <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold uppercase rounded ${TYPE_COLORS[r.connection_type] || "bg-zinc-500 text-white"}`}>
                {r.connection_type}
              </span>
            ) },
            { header: "Via", cell: (r) => <span className="text-xs">{r.via}</span> },
            { header: "Aksi", cell: (r) => (
              <Button size="sm" variant="ghost" onClick={() => navigate(`/graph?borrower=${r.borrower_a_id}`)} data-testid={`view-path-${r.borrower_a_id}`}>
                Lihat Graph →
              </Button>
            ) },
          ]}
          data={filtered}
          pageSize={15}
        />
      )}
    </div>
  );
}
